import { usePersistedJsonState } from '@betterfunnel/react-hooks';
import {
  AccountBrand,
  AccountConfig,
  AdsAccount,
  Domain,
  DynamicPage,
  Form,
  GlobalSection,
  LinkedCampaign,
  SectionType,
  SectionTypeConfig,
  SectionTypeStructureFieldList,
  TeamMember,
} from '@betterfunnel/types';
import { QueryDocumentSnapshot, onSnapshot } from 'firebase/firestore';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { AnalyticsTypeOption } from '../components/AnalyticsTypeButton';
import { getAccountBrandDocRef } from '../services/AccountBrandService';
import { getAccountConfigDocRef } from '../services/AccountConfigService';
import {
  updateAccountBrand as updateAccountBrandApi,
  updateAccountConfig as updateAccountConfigApi,
} from '../services/AccountsService';
import { activeAdsAccountsQuery } from '../services/AdsAccountsService';
import { getDomainsColRef } from '../services/DomainsService';
import { dynamicPagesQuery } from '../services/DynamicPagesService';
import { formsQuery } from '../services/FormsService';
import { globalSectionsQuery } from '../services/GlobalSectionsService';
import { activeLinkedCampaignsQuery } from '../services/LinkedCampaignsService';
import { getAccountSectionTypes } from '../services/SectionTypesService';
import { approvedTeamMembersQuery } from '../services/TeamMembersService';
import { UserContext } from './UserContext';

export type AnalyticsCustomRange = {
  startDate: string;
  endDate: string;
};

export type AnalyticsRange =
  | 'today'
  | 'yesterday'
  | 'last7Days'
  | 'last30Days'
  | 'currentMonth'
  | AnalyticsCustomRange;

interface DashboardContextProps {
  dynamicPages?: QueryDocumentSnapshot<DynamicPage, DynamicPage>[];
  selectedDynamicPage?: QueryDocumentSnapshot<DynamicPage, DynamicPage>;
  linkedCampaigns?: QueryDocumentSnapshot<LinkedCampaign, LinkedCampaign>[];
  teamMembers?: QueryDocumentSnapshot<TeamMember, TeamMember>[];
  forms?: QueryDocumentSnapshot<Form, Form>[];
  globalSections?: QueryDocumentSnapshot<GlobalSection, GlobalSection>[];

  allDomains?: QueryDocumentSnapshot<Domain, Domain>[];
  domains?: QueryDocumentSnapshot<Domain, Domain>[];
  primaryDomain?: QueryDocumentSnapshot<Domain, Domain>;
  adsAccounts?: QueryDocumentSnapshot<AdsAccount, AdsAccount>[];

  sectionTypeConfigs?: QueryDocumentSnapshot<SectionTypeConfig>[];
  getSectionTypeConfig: (
    type: SectionType | string,
  ) => SectionTypeConfig | undefined;
  getSectionListItemFieldConfig: (
    sectionType: SectionType | string,
    field: string,
  ) => SectionTypeStructureFieldList | undefined;

  analyticsType: AnalyticsTypeOption | null;
  setAnalyticsType: (type?: AnalyticsTypeOption) => void;

  accountBrand?: AccountBrand;
  updateAccountBrand: (config: Partial<AccountBrand>) => void;

  accountConfig?: AccountConfig;
  updateAccountConfig: (config: Partial<AccountConfig>) => void;

  analyticsRange: AnalyticsRange;
  setAnalyticsRange: (range: AnalyticsRange) => void;
}

export const DashboardContext = createContext<DashboardContextProps>({
  getSectionTypeConfig: () => undefined,
  getSectionListItemFieldConfig: () => undefined,

  updateAccountBrand: () => {},
  updateAccountConfig: () => {},
  analyticsType: null,
  setAnalyticsType: () => {},

  analyticsRange: 'last30Days',
  setAnalyticsRange: () => {},
});

export function DashboardContextProvider(props: any) {
  const location = useLocation();

  const { selectedAccount } = useContext(UserContext);

  const [dynamicPages, setDynamicPages] =
    useState<QueryDocumentSnapshot<DynamicPage, DynamicPage>[]>();
  const [linkedCampaigns, setLinkedCampaigns] =
    useState<QueryDocumentSnapshot<LinkedCampaign, LinkedCampaign>[]>();
  const [forms, setForms] = useState<QueryDocumentSnapshot<Form, Form>[]>();
  const [globalSections, setGlobalSections] =
    useState<QueryDocumentSnapshot<GlobalSection, GlobalSection>[]>();
  const [allDomains, setAllDomains] =
    useState<QueryDocumentSnapshot<Domain, Domain>[]>();
  const [accountBrand, setAccountBrand] = useState<AccountBrand>();
  const [accountBrandToSave, setAccountBrandToSave] =
    useState<Partial<AccountBrand>>();
  const [accountConfig, setAccountConfig] = useState<AccountConfig>();
  const [accountConfigToSave, setAccountConfigToSave] =
    useState<Partial<AccountConfig>>();
  const [sectionTypeConfigs, setSectionTypeConfigs] =
    useState<QueryDocumentSnapshot<SectionTypeConfig>[]>();
  const [analyticsRange, setAnalyticsRange] =
    usePersistedJsonState<AnalyticsRange>('analyticsRange', 'last30Days');
  const [analyticsType, _setAnalyticsType] =
    useState<AnalyticsTypeOption | null>(
      (localStorage.getItem('analyticsType') as AnalyticsTypeOption) || null,
    );
  const [teamMembers, setTeamMembers] =
    useState<QueryDocumentSnapshot<TeamMember, TeamMember>[]>();
  const [adsAccounts, setAdsAccounts] =
    useState<QueryDocumentSnapshot<AdsAccount, AdsAccount>[]>();

  const selectedDynamicPage = useMemo(() => {
    const dynamicPageId = location.pathname.split('/')[4];
    if (dynamicPageId === 'pages') {
      const dynamicPageId = location.pathname.split('/')[5];
      return dynamicPages?.find((a) => a.id === dynamicPageId);
    }
    return dynamicPages?.find((a) => a.id === dynamicPageId);
  }, [dynamicPages, location]);

  const domains = useMemo(() => {
    return allDomains?.filter((d) => d.data().status === 'approved');
  }, [allDomains]);

  const primaryDomain = useMemo(() => {
    return (
      domains?.find((d) => d.data().type === 'custom') ||
      domains?.find((d) => d.data().type === 'subdomain')
    );
  }, [domains]);

  useEffect(() => {
    if (!selectedAccount) {
      setDynamicPages(undefined);
      return;
    }

    const accountId = selectedAccount.id;
    const unsubscribe = onSnapshot(
      dynamicPagesQuery(accountId),
      (dynamicPages) => {
        setDynamicPages(dynamicPages.docs);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [selectedAccount]);

  useEffect(() => {
    if (!selectedAccount) {
      setLinkedCampaigns(undefined);
      return;
    }

    const accountId = selectedAccount.id;

    const unsubscribe = onSnapshot(
      activeLinkedCampaignsQuery(accountId),
      (linkedCampaigns) => {
        setLinkedCampaigns(linkedCampaigns.docs);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [selectedAccount]);

  useEffect(() => {
    if (!selectedAccount) {
      setAllDomains(undefined);
      return;
    }

    const accountId = selectedAccount.id;
    const domainsColRef = getDomainsColRef(accountId);

    const unsubscribe = onSnapshot(domainsColRef, (domains) => {
      setAllDomains(domains.docs);
    });

    return () => {
      unsubscribe();
    };
  }, [selectedAccount]);

  useEffect(() => {
    if (!selectedAccount) {
      setSectionTypeConfigs(undefined);
      return;
    }

    const accountId = selectedAccount.id;

    let relevant = true;
    getAccountSectionTypes(accountId).then((sectionTypes) => {
      if (!relevant) return;
      setSectionTypeConfigs(sectionTypes.docs);
    });

    return () => {
      relevant = false;
    };
  }, [selectedAccount]);

  useEffect(() => {
    if (!selectedAccount) {
      setForms(undefined);
      return;
    }

    const accountId = selectedAccount.id;
    const unsubscribe = onSnapshot(formsQuery(accountId), (forms) => {
      setForms(forms.docs);
    });

    return () => {
      unsubscribe();
    };
  }, [selectedAccount]);

  useEffect(() => {
    if (!selectedAccount) {
      setGlobalSections(undefined);
      return;
    }

    const accountId = selectedAccount.id;
    const unsubscribe = onSnapshot(
      globalSectionsQuery(accountId),
      (globalSections) => {
        setGlobalSections(globalSections.docs);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [selectedAccount]);

  useEffect(() => {
    if (!selectedAccount) {
      setAccountBrand(undefined);
      return;
    }

    const accountId = selectedAccount.id;
    const unsubscribe = onSnapshot(
      getAccountBrandDocRef(accountId),
      (accountBrand) => {
        setAccountBrand(
          accountBrand.exists() ? accountBrand.data() : undefined,
        );
      },
    );

    return () => {
      unsubscribe();
    };
  }, [selectedAccount]);

  useEffect(() => {
    if (!selectedAccount) {
      setAdsAccounts(undefined);
      return;
    }

    const accountId = selectedAccount.id;
    const unsubscribe = onSnapshot(
      activeAdsAccountsQuery(accountId),
      (adsAccounts) => {
        setAdsAccounts(adsAccounts.docs);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [selectedAccount]);

  const updateAccountBrand = useCallback((data: Partial<AccountBrand>) => {
    setAccountBrandToSave((d) => ({ ...(d || {}), ...data }));
    setAccountBrand((d) => ({ ...(d || {}), ...data }));
  }, []);

  useEffect(() => {
    if (!selectedAccount || !accountBrandToSave) return;

    const debounce = setTimeout(() => {
      updateAccountBrandApi(selectedAccount.id, accountBrandToSave);
      setAccountBrandToSave(undefined);
    }, 1500);

    return () => {
      clearTimeout(debounce);
    };
  }, [accountBrandToSave, selectedAccount]);

  useEffect(() => {
    if (!selectedAccount) {
      setAccountConfig(undefined);
      return;
    }

    const accountId = selectedAccount.id;
    const unsubscribe = onSnapshot(
      getAccountConfigDocRef(accountId),
      (accountConfig) => {
        setAccountConfig(
          accountConfig.exists() ? accountConfig.data() : undefined,
        );
      },
    );

    return () => {
      unsubscribe();
    };
  }, [selectedAccount]);

  useEffect(() => {
    if (!selectedAccount) {
      setTeamMembers(undefined);
      return;
    }

    const unsubscribe = onSnapshot(
      approvedTeamMembersQuery(selectedAccount.id),
      (teamMembers) => {
        setTeamMembers(teamMembers.docs);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [selectedAccount]);

  const updateAccountConfig = useCallback((data: Partial<AccountConfig>) => {
    setAccountConfigToSave((d) => ({
      ...(d || {}),
      ...data,
      seo: { ...(d?.seo || {}), ...data.seo },
    }));
    setAccountConfig((d) => ({
      ...(d || {}),
      ...data,
      seo: { ...(d?.seo || {}), ...data.seo },
    }));
  }, []);

  useEffect(() => {
    if (!selectedAccount || !accountConfigToSave) return;

    const debounce = setTimeout(() => {
      updateAccountConfigApi(selectedAccount.id, accountConfigToSave);
      setAccountConfigToSave(undefined);
    }, 1500);

    return () => {
      clearTimeout(debounce);
    };
  }, [accountConfigToSave, selectedAccount]);

  const setAnalyticsType = useCallback(
    (type?: AnalyticsTypeOption | null) => {
      if (type) {
        localStorage.setItem('analyticsType', type);
      } else {
        localStorage.removeItem('analyticsType');
      }

      _setAnalyticsType(type || null);
    },
    [_setAnalyticsType],
  );

  const getSectionTypeConfig = useCallback(
    (type: SectionType | string) => {
      const onlyMatchingTypes = sectionTypeConfigs?.filter(
        (s) => s.data().type === type,
      );
      return (
        onlyMatchingTypes?.find((s) => s.data().accountId) ||
        onlyMatchingTypes?.[0]
      )?.data();
    },
    [sectionTypeConfigs],
  );

  const getSectionListItemFieldConfig = useCallback(
    (sectionType: SectionType | string, field: string) => {
      const sectionTypeConfig = getSectionTypeConfig(sectionType);
      return sectionTypeConfig?.structure.fields.find(
        (f) => f.type === 'list' && f.name === field,
      ) as SectionTypeStructureFieldList | undefined;
    },
    [getSectionTypeConfig],
  );

  return (
    <DashboardContext.Provider
      value={{
        dynamicPages,
        linkedCampaigns,
        selectedDynamicPage,
        forms,
        globalSections,
        allDomains,
        domains,
        primaryDomain,
        adsAccounts,
        sectionTypeConfigs,
        getSectionTypeConfig,
        getSectionListItemFieldConfig,
        accountBrand,
        updateAccountBrand,
        accountConfig,
        updateAccountConfig,
        analyticsRange,
        setAnalyticsRange,
        analyticsType,
        setAnalyticsType,
        teamMembers,
      }}
    >
      {props.children}
    </DashboardContext.Provider>
  );
}

export function useDashboardContext() {
  return useContext(DashboardContext);
}
